import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    LinearProgress,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Button,
    CircularProgress
} from '@mui/material';
import {
    People as PeopleIcon,
    PersonAdd as PersonAddIcon,
    PersonOff as PersonOffIcon,
    Security as SecurityIcon,
    TrendingUp as TrendingUpIcon,
    BarChart as BarChartIcon,
    Notifications as NotificationsIcon,
    CheckCircle as CheckCircleIcon,
    Settings as SettingsIcon
} from '@mui/icons-material';
import { baseURL } from '../../config';
import './admin_dashboard.css';

const AdminDashboard = () => {
    const [dateFilter, setDateFilter] = useState('month');
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState({
        totalUsers: 0,
        activeUsers: 0,
        inactiveUsers: 0,
        adminUsers: 0,
        traderUsers: 0,
        newUsersThisWeek: 0,
        newUsersThisMonth: 0
    });

    // Fetch users from API
    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                console.error('No authentication token found');
                return;
            }

            const response = await fetch(`${baseURL}/api/admin/users`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (Array.isArray(data)) {
                setUsers(data);
                calculateStats(data);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const calculateStats = (usersData) => {
        const now = new Date();
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

        const totalUsers = usersData.length;
        const activeUsers = usersData.filter(u => u.status === 'active').length;
        const inactiveUsers = usersData.filter(u => u.status === 'inactive').length;
        const adminUsers = usersData.filter(u => u.role === 'Admin').length;
        const traderUsers = usersData.filter(u => u.role === 'Trader').length;

        const newUsersThisWeek = usersData.filter(u => {
            const createdDate = new Date(u.created_at);
            return createdDate >= oneWeekAgo;
        }).length;

        const newUsersThisMonth = usersData.filter(u => {
            const createdDate = new Date(u.created_at);
            return createdDate >= oneMonthAgo;
        }).length;

        setStats({
            totalUsers,
            activeUsers,
            inactiveUsers,
            adminUsers,
            traderUsers,
            newUsersThisWeek,
            newUsersThisMonth
        });
    };

    // Get recent 5 users
    const recentUsers = users
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5)
        .map(user => {
            const createdDate = new Date(user.created_at);
            const now = new Date();
            const diffTime = Math.abs(now - createdDate);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            let createdAgo;
            if (diffDays === 0) {
                createdAgo = 'Today';
            } else if (diffDays === 1) {
                createdAgo = '1 day ago';
            } else if (diffDays < 7) {
                createdAgo = `${diffDays} days ago`;
            } else if (diffDays < 30) {
                const weeks = Math.floor(diffDays / 7);
                createdAgo = weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
            } else {
                const months = Math.floor(diffDays / 30);
                createdAgo = months === 1 ? '1 month ago' : `${months} months ago`;
            }

            const fullName = user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown User';
            const nameParts = fullName.split(' ');
            const avatar = nameParts.length >= 2
                ? `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase()
                : fullName.substring(0, 2).toUpperCase();

            return {
                name: fullName,
                email: user.email,
                createdAgo,
                status: user.status === 'active' ? 'Active' : 'Inactive',
                role: user.role,
                avatar
            };
        });

    const statCards = [
        {
            title: 'Total Users',
            value: loading ? '...' : stats.totalUsers,
            icon: <PeopleIcon sx={{ fontSize: 40 }} />,
            color: '#7c3aed',
            bgColor: 'rgba(124, 58, 237, 0.1)'
        },
        {
            title: 'Active Users',
            value: loading ? '...' : stats.activeUsers,
            icon: <PersonAddIcon sx={{ fontSize: 40 }} />,
            color: '#22c55e',
            bgColor: 'rgba(34, 197, 94, 0.1)'
        },
        {
            title: 'Inactive Users',
            value: loading ? '...' : stats.inactiveUsers,
            icon: <PersonOffIcon sx={{ fontSize: 40 }} />,
            color: '#f59e0b',
            bgColor: 'rgba(245, 158, 11, 0.1)'
        },
        {
            title: 'Admin Users',
            value: loading ? '...' : stats.adminUsers,
            icon: <SecurityIcon sx={{ fontSize: 40 }} />,
            color: '#06b6d4',
            bgColor: 'rgba(6, 182, 212, 0.1)'
        }
    ];

    const getActivePercentage = () => {
        if (stats.totalUsers === 0) return 0;
        return Math.round((stats.activeUsers / stats.totalUsers) * 100);
    };

    return (
        <Box className="admin-dashboard">
            {/* Header */}
            <Box className="admin-dashboard-header">
                <Box>
                    <Typography variant="h4" className="admin-dashboard-title">
                        Admin Dashboard
                    </Typography>
                    <Typography variant="body1" className="admin-dashboard-subtitle">
                        Overview of user management and system statistics
                    </Typography>
                </Box>
                <Box className="admin-dashboard-controls">
                    <FormControl size="small" className="admin-date-filter">
                        <InputLabel>Date Filter</InputLabel>
                        <Select
                            value={dateFilter}
                            label="Date Filter"
                            onChange={(e) => setDateFilter(e.target.value)}
                        >
                            <MenuItem value="week">Last Week</MenuItem>
                            <MenuItem value="month">Last Month</MenuItem>
                            <MenuItem value="year">This Year</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* System Status Banner */}
            <Paper className="admin-status-banner">
                <Box className="admin-status-content">
                    <TrendingUpIcon className="admin-status-icon" />
                    <Typography className="admin-status-text">
                        System Status: All systems operational | User growth is up 15% compared to last month
                    </Typography>
                </Box>
            </Paper>

            {/* Statistics Cards */}
            <Grid container spacing={3} className="admin-stats-grid">
                {statCards.map((card, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                        <Card className="admin-stat-card">
                            <CardContent>
                                <Box className="admin-stat-content">
                                    <Box
                                        className="admin-stat-icon"
                                        sx={{
                                            backgroundColor: card.bgColor,
                                            color: card.color
                                        }}
                                    >
                                        {card.icon}
                                    </Box>
                                    <Box className="admin-stat-info">
                                        <Typography variant="h4" className="admin-stat-value">
                                            {card.value.toLocaleString()}
                                        </Typography>
                                        <Typography variant="body2" className="admin-stat-label">
                                            {card.title}
                                        </Typography>
                                        
                                        {/* Progress bar for active users */}
                                        {card.title === 'Active Users' && (
                                            <Box className="admin-progress-container">
                                                <Box className="admin-progress-info">
                                                    <Typography variant="body2" className="admin-progress-label">
                                                        Active Rate
                                                    </Typography>
                                                    <Typography variant="body2" className="admin-progress-percentage">
                                                        {getActivePercentage()}%
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={getActivePercentage()}
                                                    className="admin-progress-bar"
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Additional Stats */}
            <Grid container spacing={3} className="admin-additional-stats">
                <Grid item xs={12} md={6}>
                    <Paper className="admin-info-card">
                        <Typography variant="h6" className="admin-info-title">
                            Recent Activity
                        </Typography>
                        <Box className="admin-info-content">
                            <Box className="admin-info-row">
                                <Typography variant="body2" className="admin-info-label">
                                    New users this week
                                </Typography>
                                <Typography variant="body2" className="admin-info-value">
                                    {loading ? '...' : stats.newUsersThisWeek}
                                </Typography>
                            </Box>
                            <Box className="admin-info-row">
                                <Typography variant="body2" className="admin-info-label">
                                    New users this month
                                </Typography>
                                <Typography variant="body2" className="admin-info-value">
                                    {loading ? '...' : stats.newUsersThisMonth}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper className="admin-info-card">
                        <Typography variant="h6" className="admin-info-title">
                            User Roles
                        </Typography>
                        <Box className="admin-info-content">
                            <Box className="admin-info-row">
                                <Typography variant="body2" className="admin-info-label">
                                    Traders
                                </Typography>
                                <Typography variant="body2" className="admin-info-value">
                                    {loading ? '...' : stats.traderUsers}
                                </Typography>
                            </Box>
                            <Box className="admin-info-row">
                                <Typography variant="body2" className="admin-info-label">
                                    Admins
                                </Typography>
                                <Typography variant="body2" className="admin-info-value">
                                    {loading ? '...' : stats.adminUsers}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            </Grid>


            {/* Recent Users Section */}
            <Grid container spacing={3} sx={{ mt: 3 }}>
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, background: '#ffffff' }}>
                        <Typography variant="h5" sx={{ mb: 3, color: '#1e293b', fontWeight: 700 }}>
                            Recent Users
                        </Typography>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                <CircularProgress />
                            </Box>
                        ) : recentUsers.length === 0 ? (
                            <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', py: 4 }}>
                                No users found
                            </Typography>
                        ) : (
                            <>
                                {recentUsers.map((user, index) => (
                                    <Box
                                        key={index}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            mb: 2,
                                            pb: 2,
                                            borderBottom: index < recentUsers.length - 1 ? '1px solid #f1f5f9' : 'none'
                                        }}
                                    >
                                        <Box sx={{
                                            width: 44,
                                            height: 44,
                                            borderRadius: '10px',
                                            background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontWeight: 700,
                                            fontSize: '0.875rem',
                                            boxShadow: '0 4px 12px rgba(124, 58, 237, 0.25)'
                                        }}>
                                            {user.avatar}
                                        </Box>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.9375rem' }}>
                                                {user.name}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8125rem' }}>
                                                {user.email} • Created {user.createdAgo}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={user.status}
                                            size="small"
                                            sx={{
                                                backgroundColor: user.status === 'Active' ? '#dcfce7' : '#fee2e2',
                                                color: user.status === 'Active' ? '#166534' : '#991b1b',
                                                borderRadius: '8px',
                                                fontWeight: 600,
                                                fontSize: '0.75rem'
                                            }}
                                        />
                                    </Box>
                                ))}
                                <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', mt: 2, pt: 2, borderTop: '1px solid #f1f5f9' }}>
                                    Showing {recentUsers.length} of {stats.totalUsers} recent users
                                </Typography>
                            </>
                        )}
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, background: '#ffffff', mb: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: '#1e293b', fontWeight: 700 }}>
                            Quick Actions
                        </Typography>
                        <Button
                            variant="contained"
                            fullWidth
                            sx={{
                                background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 600,
                                py: 1.5,
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #6d28d9 0%, #4c1d95 100%)',
                                }
                            }}
                        >
                            Create New User
                        </Button>
                    </Paper>
                    
                    <Paper sx={{ p: 3, background: '#ffffff' }}>
                        <Typography variant="h6" sx={{ mb: 2, color: '#1e293b' }}>
                            System Health
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Box sx={{ 
                                width: 24, 
                                height: 24, 
                                borderRadius: '4px', 
                                backgroundColor: '#dcfce7',
                                color: '#166534',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                ✓
                            </Box>
                            <Typography variant="body2" sx={{ flex: 1, color: '#64748b' }}>
                                Database
                            </Typography>
                            <Chip label="Healthy" size="small" sx={{ 
                                backgroundColor: '#dcfce7', 
                                color: '#166534',
                                borderRadius: '12px'
                            }} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Box sx={{ 
                                width: 24, 
                                height: 24, 
                                borderRadius: '4px', 
                                backgroundColor: '#dbeafe',
                                color: '#1e40af',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                ⚙
                            </Box>
                            <Typography variant="body2" sx={{ flex: 1, color: '#64748b' }}>
                                API Services
                            </Typography>
                            <Chip label="Operational" size="small" sx={{ 
                                backgroundColor: '#dbeafe', 
                                color: '#1e40af',
                                borderRadius: '12px'
                            }} />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ 
                                width: 24, 
                                height: 24, 
                                borderRadius: '4px', 
                                backgroundColor: '#dcfce7',
                                color: '#166534',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                ✓
                            </Box>
                            <Typography variant="body2" sx={{ flex: 1, color: '#64748b' }}>
                                User Authentication
                            </Typography>
                            <Chip label="Active" size="small" sx={{ 
                                backgroundColor: '#dcfce7', 
                                color: '#166534',
                                borderRadius: '12px'
                            }} />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AdminDashboard;
